import React, { useCallback, useState } from 'react';
import { FormInput } from 'components/form';
import { Edit, Delete } from '@mui/icons-material';
import { Box, FormControlLabel, IconButton, Switch, Tooltip } from '@mui/material';

import { CloseReason } from '../../../types';
import { useBPFCloseReasonsMutations } from '../../../api';

interface Props {
  reason: CloseReason;
}

export const CloseReasonField = ({ reason }: Props) => {
  const { loading, actions } = useBPFCloseReasonsMutations();

  const [reasonLabel, setReasonLabel] = useState(reason.label);

  const saveLabel = useCallback(async () => {
    await actions.handleUpdateCloseReasonLabel(reason.id, reasonLabel.trim());
  }, [actions, reason.id, reasonLabel]);

  const isDirty = reasonLabel.trim() !== reason.label.trim();

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '2.5rem 2.5rem 1fr 6rem', gap: '0.5rem' }}>
      <Tooltip title="Remove reason">
        <IconButton
          size="small"
          color="error"
          loading={loading.isDeleting}
          className="icon-btn-bordered"
          onClick={async () => {
            await actions.handleDeleteCloseReason(reason.id, api => ({
              awaitRefetchQueries: true,
              refetchQueries: [api.fetchCloseReasons],
            }));
          }}
        >
          <Delete color="error" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Save Label">
        <IconButton
          size="small"
          onClick={saveLabel}
          className="icon-btn-bordered"
          loading={loading.isLabelUpdating}
          disabled={!isDirty || loading.isLabelUpdating}
        >
          <Edit color={isDirty ? 'primary' : 'disabled'} />
        </IconButton>
      </Tooltip>

      <FormInput
        variant="outlined"
        value={reasonLabel}
        placeholder="Enter reason"
        onChange={value => {
          setReasonLabel(value);
        }}
      />

      <Box>
        <FormControlLabel
          control={<Switch />}
          checked={reason.isActive}
          disabled={loading.isStatusUpdating}
          label={reason.isActive ? 'Active' : 'Inactive'}
          onChange={async (_, checked) => {
            await actions.handleUpdateCloseReasonStatus(reason.id, checked, api => ({
              awaitRefetchQueries: true,
              refetchQueries: [api.fetchCloseReasons],
            }));
          }}
        />
      </Box>
    </Box>
  );
};
